import { useMemo, useState } from "react";
import type { EstimateType } from "@rebuild/shared";
import OrganizationSelector from "../../components/ui/utils/OrganizationSelector";
import { useOrganizationSelect } from "../../hooks/useOrganizationSelect";
import Chip from "./Chip";
import { ESTIMATE_TYPES } from "./customContext.constants";
import type { DraftEntry } from "./customContext.types";

interface EditModalProps {
  title: string;
  draft: DraftEntry;
  organizations: ReturnType<typeof useOrganizationSelect>["organizations"];
  orgNameById: Map<string, string>;
  onChange: (draft: DraftEntry) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
  isDisabled: boolean;
  disabledReason?: string;
}

function EditModal({
  title,
  draft,
  organizations,
  orgNameById,
  onChange,
  onClose,
  onSave,
  isSaving,
  isDisabled,
  disabledReason,
}: EditModalProps) {
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const availableOrganizations = useMemo(
    () => organizations.filter((org) => !draft.organizationIds.includes(org.organizationId)),
    [organizations, draft.organizationIds],
  );

  const addOrganization = (organizationId: string | undefined) => {
    if (!organizationId || draft.organizationIds.includes(organizationId)) return;
    onChange({ ...draft, organizationIds: [...draft.organizationIds, organizationId] });
  };

  const removeOrganization = (organizationId: string) => {
    onChange({ ...draft, organizationIds: draft.organizationIds.filter((id) => id !== organizationId) });
  };

  const toggleEstimateType = (estimateType: EstimateType) => {
    const isSelected = draft.estimateTypes.includes(estimateType);
    onChange({
      ...draft,
      estimateTypes: isSelected ? draft.estimateTypes.filter((type) => type !== estimateType) : [...draft.estimateTypes, estimateType],
    });
  };

  const onSaveClick = () => {
    if (isDisabled) {
      setHasAttemptedSave(true);
      return;
    }
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-lg shadow-lg w-[1100px] max-w-[95vw] h-[85vh] max-h-[900px] relative flex flex-col">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl">
          ×
        </button>
        <div className="p-6 flex flex-col flex-1 min-h-0">
          <h2 className="text-xl font-bold text-gray-800 mb-6">{title}</h2>
          <div className="flex gap-6 flex-1 min-h-0">
            {/* Left: organizations + estimate types */}
            <div className="w-80 shrink-0 space-y-4 overflow-y-auto">
              <div>
                <OrganizationSelector
                  label="Organizations"
                  organizations={availableOrganizations}
                  selectedOrganization={undefined}
                  onSelect={(org) => addOrganization(org?.organizationId)}
                />
                <div className="flex flex-wrap gap-1 mt-2">
                  {draft.organizationIds.length === 0 ? (
                    <span className="text-xs text-gray-400">No organizations selected.</span>
                  ) : (
                    draft.organizationIds.map((orgId) => (
                      <Chip key={orgId} label={orgNameById.get(orgId) ?? orgId} onRemove={() => removeOrganization(orgId)} size="md" />
                    ))
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estimate Types</label>
                <div className="flex flex-wrap gap-2">
                  {ESTIMATE_TYPES.map((estimateType) => {
                    const isSelected = draft.estimateTypes.includes(estimateType);
                    return (
                      <button
                        key={estimateType}
                        type="button"
                        onClick={() => toggleEstimateType(estimateType)}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          isSelected ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {estimateType}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Right: title + custom context */}
            <div className="flex flex-col flex-1 min-h-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md p-2 text-sm mb-4"
                value={draft.title}
                onChange={(e) => onChange({ ...draft, title: e.target.value })}
                placeholder="e.g. Dehumidifier mapping for water mitigation"
              />
              <label className="block text-sm font-medium text-gray-700 mb-1">Context</label>
              <textarea
                className="w-full flex-1 min-h-0 resize-none border border-gray-300 rounded-md p-3 text-sm"
                value={draft.context}
                onChange={(e) => onChange({ ...draft, context: e.target.value })}
                placeholder="e.g. Dehumidifier model XYZ maps to the dehumidifier template, 110-159 ppd permutation."
              />
            </div>
          </div>
          {hasAttemptedSave && isDisabled && disabledReason && <p className="mt-6 -mb-2 text-sm text-red-600">{disabledReason}</p>}
          <button
            onClick={onSaveClick}
            disabled={isSaving || (hasAttemptedSave && isDisabled)}
            className="mt-6 w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditModal;
